import { Injectable } from '@angular/core';

import { StorageApiService } from 'src/app/api/services/storage-api.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import {
  StorageFile,
  StorageUtilsService,
} from 'src/app/storage/services/storage-utils.service';
import { ComponentsService } from '../../services/components.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { TemplateEditorService } from '../../services/template-editor.service';

export interface ImageGeneration {
  url: string;
  filename: string;
  imageId?: string;
  displaySrc?: string;
  thumbnailUrl?: string;
  usingFull?: boolean;
  previewFailed?: boolean;
  justGenerated?: boolean;
  loaded?: boolean;
  feedbackRating?: 'good' | 'bad';
}

export interface ImageAiStorageFile {
  name: string;
}

export interface ImageAiShowOptions {
  onSelect: (file: ImageAiStorageFile) => void;
  width: number;
  height: number;
}

export type ImageDimensions = { width: number; height: number };

const RATIO_TABLE: { ratio: number; label: string; dims: ImageDimensions }[] = [
  { ratio: 1, label: '1:1', dims: { width: 1024, height: 1024 } },
  { ratio: 4 / 3, label: '4:3', dims: { width: 1024, height: 768 } },
  { ratio: 5 / 4, label: '5:4', dims: { width: 1024, height: 819 } },
  { ratio: 16 / 9, label: '16:9', dims: { width: 1024, height: 576 } },
  { ratio: 2, label: '2:1', dims: { width: 1024, height: 512 } },
  { ratio: 3 / 4, label: '3:4', dims: { width: 768, height: 1024 } },
  { ratio: 4 / 5, label: '4:5', dims: { width: 819, height: 1024 } },
  { ratio: 9 / 16, label: '9:16', dims: { width: 576, height: 1024 } },
  { ratio: 1 / 2, label: '1:2', dims: { width: 512, height: 1024 } },
];

/** Used when the target size is missing or invalid (16:9 at long edge 1024). */
const FALLBACK_DIMENSIONS: ImageDimensions = { width: 1024, height: 576 };

const FALLBACK_ASPECT_RATIO = '16:9';

@Injectable({
  providedIn: 'root',
})
export class ImageAiService {
  static readonly GENERATIONS_FOLDER = 'rise-ai-uploads/edit-tools/';

  constructor(
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private trackerService: TrackerService,
    private templateEditorService: TemplateEditorService,
    private storageApiService: StorageApiService,
    private storageUtilsService: StorageUtilsService,
  ) {}

  async getRecentGenerations(): Promise<ImageGeneration[]> {
    const response = await this.storageApiService.list({
      folderPath: ImageAiService.GENERATIONS_FOLDER,
    });
    const files = response?.files ?? [];

    return files
      .filter((file) => this.isGeneratedImage(file))
      .sort((a, b) => (b.timeCreated?.value ?? 0) - (a.timeCreated?.value ?? 0))
      .map((file) => this.toGeneration(file));
  }

  private isGeneratedImage(file: StorageFile): boolean {
    return (
      file.name !== ImageAiService.GENERATIONS_FOLDER &&
      !!file.contentType &&
      file.contentType.startsWith('image/')
    );
  }

  private toGeneration(file: StorageFile): ImageGeneration {
    const url = this.storageUtilsService.storageFileUrlFor(
      this.storageUtilsService.getFilePath(file),
    );

    const thumbnailUrl = file.metadata?.thumbnail;

    return {
      url,
      filename: this.storageUtilsService.fileNameOf(file.name),
      thumbnailUrl,
      displaySrc: thumbnailUrl || url,
    };
  }

  static dimensionsForAspectRatio(
    width: number,
    height: number,
  ): ImageDimensions {
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return FALLBACK_DIMENSIONS;
    }

    const ratio = width / height;

    return RATIO_TABLE.reduce((best, entry) =>
      Math.abs(entry.ratio - ratio) < Math.abs(best.ratio - ratio)
        ? entry
        : best,
    ).dims;
  }

  static aspectRatioLabel(width: number, height: number): string {
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return FALLBACK_ASPECT_RATIO;
    }

    const ratio = width / height;

    return RATIO_TABLE.reduce((best, entry) =>
      Math.abs(entry.ratio - ratio) < Math.abs(best.ratio - ratio)
        ? entry
        : best,
    ).label;
  }

  showGenerateImage(addFile: (file: ImageAiStorageFile) => void) {
    const dims = this.targetDimensions();

    this.componentsService.editComponent(
      {
        type: 'rise-image-generate-ai',
      },
      {
        onSelect: (selected: ImageAiStorageFile) => {
          addFile(selected);
          this.trackerService.presentationEvent(
            'AI Image Added',
            this.templateEditorService.presentation.id,
            this.templateEditorService.presentation.name,
          );
        },
        width: dims.width,
        height: dims.height,
      } satisfies ImageAiShowOptions,
    );
    this.componentsService.removePageFromStack('rise-media-selector');
    this.componentsService.showContextualToolbar({ id: null });
  }

  private targetDimensions(): ImageDimensions {
    const pages = this.componentsService.pages ?? [];
    const openerId = pages.at(-2)?.id ?? null;

    const width = parseFloat(
      this.attributeDataService.getCustomization(openerId, 'width'),
    );
    const height = parseFloat(
      this.attributeDataService.getCustomization(openerId, 'height'),
    );

    return ImageAiService.dimensionsForAspectRatio(width, height);
  }
}

export function generateSessionId(): string {
  return (
    'session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11)
  );
}
